/**
 * "Your regular round", applied to a setup draft.
 *
 * Play Golf's regular-round row is a round already half set up: the course you
 * play most, off the tee you last played it from, with you already in and the
 * players you usually play with offered on the players step.
 *
 * It used to arrive as navigation state, which meant Play Golf had to AWAIT
 * `/me/regulars` before it could move — a tap that did nothing visible for as
 * long as the request took. The tap is instant now and the answer is applied
 * here when it lands, which is why this has to cope with landing LATE.
 */

/**
 * @param {object} draft the wizard's current tournament data
 * @param {object} body  the `/me/regulars` payload: { me, players, course }
 * @returns {object} the draft to use — the same object when there is nothing
 *                   to add, so a caller can skip a render
 */
export function applyRegularRound(draft, body) {
    if (!draft || !body) return draft;

    // ONLY ONTO AN UNTOUCHED FORM. On a weak connection this can land several
    // seconds after the screen did, by which time somebody may have chosen a
    // course or started picking players. Overwriting that would be the worst
    // kind of help, so a field that has been filled is left exactly as it is.
    const next = { ...draft };
    let changed = false;

    const withSelf = seedSelf(draft, body.me);
    if (withSelf !== draft) {
        next.players = withSelf.players;
        changed = true;
    }

    if (body.course?.id && !(draft.courses || []).length) {
        next.courses = [{
            id: body.course.id,
            name: body.course.name,
            sequence_number: 1,
            // The tee is part of the answer: "the course I play, off the tee I
            // play it from". Without it the round opens on the course's
            // default, which is not where this player plays.
            tee_id: body.course.tee_id || null,
            tee_name: body.course.tee_name || null,
            tees_in_play: body.course.tee_id ? [body.course.tee_id] : [],
        }];
        changed = true;
    }

    return changed ? next : draft;
}

/**
 * Whoever sets a round up is playing in it — a quick round, a competition or a
 * matchplay alike, not only "your regular round", which used to be the one way
 * in that entered you. So every setup draft starts with you on the roster;
 * remove yourself and you are the first chip on the offer row.
 *
 * The same "untouched form" rule as above: a roster somebody has started is
 * left alone, because this can land after they have. A ROLL UP is skipped —
 * the organiser is putting a sheet up for others to claim, and its roster is
 * whoever claims a slot.
 *
 * @returns {object} the draft to use — the same object when nothing changes
 */
export function seedSelf(draft, me) {
    if (!draft || !me) return draft;
    if ((draft.players || []).length) return draft;
    if (draft.signup_mode === 'roll_up') return draft;
    return { ...draft, players: [me] };
}

/** The pool offered on the players step: you first, then your regulars. */
export const regularsPool = (body) => (body?.me
    ? [body.me, ...(body.players || [])]
    : (body?.players || []));
